import {
    Box,
    FileDropzone,
    FileInput,
    FileTokenGroup,
    FileTokenGroupProps,
    SpaceBetween,
} from "@amzn/awsui-components-react";
import { CustomFileFormFieldProps } from "interfaces";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";

const getStringPath: string = "common.component.customForms.customFileDropzone";
const getStringPathList: string = `${getStringPath}.fileList`;

/**
 * A FormField that provides a file dropzone input
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 * @param {string} errorText - Error message to display
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} [testId] - Test identifier for the component
 * @param {string} [accept] - File types to accept
 *
 * @returns {JSX.Element} A FormField component with a FileDropzone
 */
const CustomFileDropzone = ({
    label,
    errorText,
    warningText,
    description,
    info,
    value,
    setValue,
    testId,
    accept,
    multiple,
    touchpoint,
}: CustomFileFormFieldProps) => {
    const filterFiles = (indexToRemove: number) => {
        return value.filter((_, index) => index !== indexToRemove);
    };

    const trackerId = useTrackerId();

    /**
     * Track custom uxm event for file change
     */
    const onChange = ({ detail }: { detail: { value: File[] } }) => {
        setValue([...value, ...detail.value]);

        const eventData: Record<string, unknown> = {
            action: "file-change",
            files: detail.value,
        };

        trackCustomEvent(eventData, touchpoint, trackerId);
    };

    /**
     * Track custom uxm event for file removal
     */
    const onDismiss = ({ detail }: { detail: FileTokenGroupProps.DismissDetail }) => {
        setValue(filterFiles(detail.fileIndex));

        const eventData: Record<string, unknown> = {
            action: "file-removal",
        };

        trackCustomEvent(eventData, touchpoint, trackerId);
    };

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={
                <SpaceBetween size="l">
                    <FileDropzone onChange={onChange}>
                        <SpaceBetween size="xxs" alignItems="center">
                            <Box color="inherit">{getString(`${getStringPath}.dropFiles`)}</Box>
                            <FileInput value={[]} onChange={onChange} accept={accept} multiple={multiple}>
                                {getString(`${getStringPath}.chooseFile`)}
                            </FileInput>
                        </SpaceBetween>
                    </FileDropzone>
                    <FileTokenGroup
                        items={value.map((file) => ({ file }))}
                        onDismiss={onDismiss}
                        i18nStrings={{
                            removeFileAriaLabel: () => getString(`${getStringPathList}.removeList`),
                            limitShowFewer: getString(`${getStringPathList}.showLess`),
                            limitShowMore: getString(`${getStringPathList}.showMore`),
                        }}
                    />
                </SpaceBetween>
            }
        />
    );
};

export default CustomFileDropzone;
